import React, { useCallback, useEffect, useMemo, useRef } from "react"
import styled, { keyframes } from "styled-components"
import BodyText, { BodyTextProps } from "components/Typography/BodyText"
import SuccessIcon from "components/icons/SuccessIcon"
import AttentionIcon from "components/icons/AttentionIcon"
import ErrorIcon from "components/icons/ErrorIcon"
import InfoIcon from "components/icons/InfoIcon"
import clsx from "clsx"

const Keyframe = keyframes`
  0% {
    right: 0px;
    transform: translateX(100%);
  }
  10% {
    right: 48px;
    transform: translateX(0%);
  }
  90% {
    right: 48px;
    transform: translateX(0%);
  }
  100% {
    right: 0px;
    transform: translateX(100%);
  }
`

const fadeIn = keyframes`
  0% {
    right: 0px;
    transform: translateX(100%);
  }
  100% {
    right: 48px;
    transform: translateX(0%);
  }
`

const fadeOut = keyframes`
  0% {
    right: 48px;
    transform: translateX(0%);
  }
  100% {
    right: 0px;
    transform: translateX(100%);
  }
`

const StyledToast = styled.div`
  display: flex;
  align-items: center;
  position: fixed;
  gap: 8px;
  padding: 24px;
  top: 48px;
  max-width: 584px;
  min-height: 72px;
  border-radius: 12px;
  background: ${(props) => props.theme.palette.white};
  z-index: 1010;
  right: 0px;
  transform: translateX(100%);

  &.fade-in {
    animation: ${fadeIn} 1s forwards;
  }

  &.fade-out {
    animation: ${fadeOut} 0.25s forwards;
  }

  &.keyframe {
    animation: ${Keyframe} 5s;
  }

  &.error {
    border: 1px solid ${(props) => props.theme.palette.error};
  }

  &.success {
    border: 1px solid ${(props) => props.theme.palette.success};
  }

  &.attention {
    border: 1px solid ${(props) => props.theme.palette.alert};
  }

  &.info {
    border: 1px solid ${(props) => props.theme.palette.primary_dark};
  }

  &.fade-in-success {
    max-width: 100%;
    border: 1px solid ${(props) => props.theme.palette.success};
    animation: ${fadeIn} 1s forwards;
  }

  &.fade-in-error {
    max-width: 100%;
    border: 1px solid ${(props) => props.theme.palette.error};
    animation: ${fadeIn} 1s forwards;
  }

  &.no-animation-success {
    max-width: 100%;
    border: 1px solid ${(props) => props.theme.palette.success};
    animation: ${fadeIn} 0s forwards;
  }
`

const ToastIcon = styled.div`
  color: ${(props) => props.theme.palette.success};
  font-size: 24px;
  width: 24px;
  height: auto;
`

type Variant = "success" | "error" | "attention" | "info" | "fade-in-success" | "fade-in-error" | "no-animation-success"

interface VariantOptions {
  icon: React.ReactNode
  color: BodyTextProps["color"]
}

interface ToastProps {
  children: React.ReactNode
  className?: string
  testId?: string
  variant?: Variant
  persistent?: boolean
  closeOnClickOutside?: boolean
  onClickOutside?: () => void
  isOpen?: boolean
  onClose?: () => void
  isClosing?: boolean
}

const VARIANT_MAPPING: Record<Variant, VariantOptions> = {
  error: {
    icon: <ErrorIcon />,
    color: "error"
  },
  success: {
    icon: <SuccessIcon />,
    color: "success"
  },
  info: {
    icon: <InfoIcon />,
    color: "primary-dark"
  },
  attention: {
    icon: <AttentionIcon />,
    color: "informative"
  },
  "fade-in-success": {
    icon: <SuccessIcon />,
    color: "success"
  },
  "fade-in-error": {
    icon: <ErrorIcon />,
    color: "error"
  },
  "no-animation-success": {
    icon: <SuccessIcon />,
    color: "success"
  }
}

const Toast = (props: ToastProps) => {
  const {
    children,
    className,
    testId,
    variant = "success",
    persistent = false,
    closeOnClickOutside = false,
    onClose,
    isClosing,
    ...rest
  } = props

  const { icon, color } = VARIANT_MAPPING[variant]

  const toastRef = useRef<HTMLDivElement | null>(null)

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      let timeoutId: NodeJS.Timeout | null = null

      if (toastRef.current && event.target instanceof Node && !toastRef.current.contains(event.target)) {
        timeoutId = setTimeout(() => {
          onClose?.()
        }, 250)
      }

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
      }
    },
    [onClose]
  )

  useEffect(() => {
    if (closeOnClickOutside) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      if (closeOnClickOutside) {
        document.removeEventListener("mousedown", handleClickOutside)
      }
    }
  }, [handleClickOutside, closeOnClickOutside])

  const animationClass = useMemo(() => {
    return clsx({
      "fade-out": isClosing,
      "fade-in": persistent && !isClosing,
      keyframe: !persistent && !isClosing
    })
  }, [isClosing, persistent])

  return (
    <>
      <StyledToast ref={toastRef} className={clsx(className, animationClass, variant)} data-testid={testId} {...rest}>
        <ToastIcon className="card-icon">{icon}</ToastIcon>
        <BodyText color={color} size="medium" weight="bold">
          {children}
        </BodyText>
      </StyledToast>
    </>
  )
}

export default Toast
